import { useRouter } from 'next/router';

import type { SitewideFooterProps } from '@motorway/mw-dealer-shared-components/components';
import { SitewideFooter as SharedComponentsSitewideFooter } from '@motorway/mw-dealer-shared-components/components';

import cypressIds from 'Components/cypress_ids.json';
import { triggerSitewideFooterLinkClickEvent } from 'Services/analytics/events/sitewideFooterEvents';
import { isTestEnv } from 'Utilities/helpers';
import useIsAuth from 'Utilities/hooks/useIsAuth';
import { routeWithErrorHandling } from 'Utilities/routes.helpers';
import { shouldUseStaticPageHref } from 'Utilities/staticPageSlugs';

type FooterProps = { beforeFixedBottomBar?: boolean; fixedBottomBarClassName?: string };

type OnLinkClickHandler = NonNullable<SitewideFooterProps['handlers']>['onLinkClick'];

export const SitewideFooter = ({
	beforeFixedBottomBar = false,
	fixedBottomBarClassName = '',
}: FooterProps): React.ReactElement => {
	const isUserAuthed = useIsAuth();
	const router = useRouter();

	const footerConfig: SitewideFooterProps = {
		dataAttributes: { cy: cypressIds.sitewide.footer },
		fixedBottomBar: { beforeFixedBottomBar, fixedBottomBarClassName },
		handlers: {
			onLinkClick: ((e, { as, href, label }) => {
				const asPath = as?.() || href;
				// Static pages (cookies/terms/privacy/etc.) are served via a next.config
				// rewrite (e.g. /cookies -> /static_page). Client-side navigation must target
				// the real page component (`/static_page`) as `href` with the public URL as
				// `as` — Next does not apply config rewrites to the `/_next/data/*.json`
				// route, so navigating to `/cookies` directly 404s the data fetch and the
				// route change stalls. Non-rewritten pages navigate to their own route.
				const path = shouldUseStaticPageHref(asPath) ? '/static_page' : href;

				e.preventDefault();
				triggerSitewideFooterLinkClickEvent(label);

				// Footer links are always cross-page navigations, so force a full (non-shallow)
				// transition. Shallow routing skips data fetching and can leave the previous
				// page component mounted when navigating away from a dynamic route
				// (e.g. /vehicles/[enquiryId]), which manifests as the URL changing without
				// the page content updating.
				void routeWithErrorHandling(router, { as: asPath, path, shallow: false });
			}) satisfies OnLinkClickHandler,
		},
		isAuth: isUserAuthed,
		isTestEnv,
	};

	return <SharedComponentsSitewideFooter {...footerConfig} />;
};
